// Monitoring-readiness validation run by the EXR mock backend (ExrMockInterceptor) when a season is transitioned to
// Monitoring. In the real system this check lives inside the backend transition endpoint, not on the client.
// TODO: Replace with the real EXR backend validation once the EXR season-state transition endpoint exists. (GMTEC-20689)
import { PlanType } from '@coin/shared/core/util-enums';
import { ExceptionalRecognitionPaymentCategory } from '@coin/shared/exceptional-recognitions/util';
import { ExceptionalRecognitionPlan } from '../models/exceptional-recognition-plan.model';

// Each entry pairs the season-settings field with its label translation key, so the rejected transition can list the
// unmet requirements back to the admin. The labels mirror the EXR season-settings config.
const EXR_MONITORING_REQUIRED_FIELDS: { settingsKey: string; label: string }[] = [
  { settingsKey: 'fxDateAt', label: 'season-mgmt.special-payment.season-setting.fx-date' },
  { settingsKey: 'rewardDateAt', label: 'season-mgmt.special-payment.season-setting.reward-date' },
  { settingsKey: 'exrCAndBRuleSetId', label: 'season-mgmt.exceptional-recognition.season-setting.cand-b-rule-set' },
  {
    settingsKey: 'cAndBCreationMailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.cand-b-creation-mail-template'
  },
  {
    settingsKey: 'nMinus2MailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.nminus2-mail-template'
  },
  { settingsKey: 'hoHMailTemplateId', label: 'season-mgmt.exceptional-recognition.season-setting.hoh-mail-template' },
  {
    settingsKey: 'globalHrBpMailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.global-hr-bp-mail-template'
  },
  {
    settingsKey: 'cAndBApproverMailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.cand-b-approver-mail-template'
  },
  {
    settingsKey: 'headOfHrMailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.head-of-hr-mail-template'
  },
  {
    settingsKey: 'headOfMaMailTemplateId',
    label: 'season-mgmt.exceptional-recognition.season-setting.head-of-ma-mail-template'
  }
];

// Season-level fields that only become required once a non-excluded Transformation Incentive plan exists.
const EXR_TRANSFORMATION_INCENTIVE_REQUIRED_FIELDS: { settingsKey: string; label: string }[] = [
  {
    settingsKey: 'transformationIncentiveHeadOfHrEmployeeId',
    label: 'season-mgmt.exceptional-recognition.season-setting.head-of-hr'
  },
  {
    settingsKey: 'cfoApprovalThresholdAmount',
    label: 'season-mgmt.exceptional-recognition.season-setting.cfo-approval-threshold'
  },
  {
    settingsKey: 'transformationIncentiveHeadOfMaEmployeeId',
    label: 'season-mgmt.exceptional-recognition.season-setting.head-of-ma'
  }
];

// Returns the labels of the unmet Monitoring requirements; an empty array means the season may move to Monitoring.
export function getExrMonitoringMissingRequirements(
  settings: Record<string, unknown> | null | undefined,
  plans: ExceptionalRecognitionPlan[]
): string[] {
  const values = settings ?? {};
  const activePlans = plans.filter(plan => plan.type !== PlanType.Default && !plan.isExcluded);
  const hasTransformationIncentivePlan = activePlans.some(
    plan => plan.category === ExceptionalRecognitionPaymentCategory.TransformationIncentive
  );
  const requiredFields = hasTransformationIncentivePlan
    ? [...EXR_MONITORING_REQUIRED_FIELDS, ...EXR_TRANSFORMATION_INCENTIVE_REQUIRED_FIELDS]
    : EXR_MONITORING_REQUIRED_FIELDS;

  const missingSettings = requiredFields
    .filter(({ settingsKey }) => values[settingsKey] == null || values[settingsKey] === '')
    .map(({ label }) => label);
  // Plan requirements come back as plain sentences, like a backend validation message would.
  const missingPlans = activePlans.length
    ? activePlans.flatMap(getMissingPlanFields)
    : ['At least one plan that is not excluded'];
  return [...missingSettings, ...missingPlans];
}

// Every plan needs a category and compensation type; Transformation Incentive runs on season-level notifications, so
// only the other categories need workflow, letter and task templates. (GMTEC-20689)
function getMissingPlanFields(plan: ExceptionalRecognitionPlan): string[] {
  const isTransformationIncentive = plan.category === ExceptionalRecognitionPaymentCategory.TransformationIncentive;
  const fields: [string, boolean][] = [
    ['category', !plan.category],
    ['compensation type', !plan.allowedCompensationTypes?.length],
    ['workflow', !isTransformationIncentive && !plan.workflowId],
    ['letter template', !isTransformationIncentive && !plan.letterTemplateId],
    ['task templates', !isTransformationIncentive && !plan.taskTemplateIds?.length]
  ];
  return fields.filter(([, isMissing]) => isMissing).map(([field]) => `Plan "${plan.title}": ${field}`);
}
